import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

const TRANSIENT_STATUS = [502, 503, 504];
const MAX_RETRIES = 2;

/** True only for genuinely temporary problems: network drop, timeout, cold start. */
function isTransient(error) {
  if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") return true;
  if (!error.response) return true;
  return TRANSIENT_STATUS.includes(error.response.status);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("artpart_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error.config || {};

    if (error.response?.status === 401 && !config.url?.includes("/auth/login")) {
      localStorage.removeItem("artpart_token");
      if (window.location.pathname.startsWith("/admin") && !window.location.pathname.endsWith("/login")) {
        window.location.replace("/admin/login");
      }
      return Promise.reject(error);
    }

    const retryable = isTransient(error) && (config.method || "get").toLowerCase() === "get";
    config.__retryCount = config.__retryCount || 0;
    if (retryable && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      const delay = 600 * 2 ** (config.__retryCount - 1);
      await new Promise((r) => setTimeout(r, delay));
      return api(config);
    }
    return Promise.reject(error);
  }
);

/** Turn any error into a German, non-technical message. Never leaks payload data. */
export function apiError(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "Ungültige Eingabe.")).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
}

export function friendlyError(error) {
  if (!error) return "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
  if (error.response?.data?.detail) return apiError(error.response.data.detail);
  if (isTransient(error)) return "Der Server ist gerade nicht erreichbar. Bitte versuche es in einem Moment erneut.";
  return "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
}

/** Log without customer data: message text and payloads are deliberately omitted. */
export function logIssue(context, error) {
  const status = error?.response?.status ?? error?.code ?? "unknown";
  // eslint-disable-next-line no-console
  console.warn(`[ArtPart] ${context} failed (${status})`);
}

export function mediaUrl(mediaId, variant = "md") {
  return mediaId ? `${API}/media/${mediaId}?v=${variant}` : "";
}

export function adminMediaUrl(mediaId, variant = "md") {
  return mediaId ? `${API}/admin/media/${mediaId}/file?v=${variant}` : "";
}
